import React from 'react';
import Link from 'next/link';
import { format } from 'date-fns';

import { Movie } from '@app/types/movie';
import { Cinema } from '@app/types/cinema';
import Badge from '@app/components/badge';
import Image from '@app/components/image';
import { getTime, GetRatingDetails } from '@app/utils';

import {
  MovieDetailSlideStyled,
  MovieDetailContentBlockStyled,
  CinemaDetailContentStyled,
  ComingSoonTextStyled,
} from './movie-detail-slide-styled';

type MovieDetailSlideProps = {
  movie: Movie;
  link?: boolean;
  cinema?: Array<Cinema>;
};

type LinkedProps = {
  children: JSX.Element;
  href: string;
};

const MovieDetailSlide = ({ movie, cinema, link = true }: MovieDetailSlideProps): JSX.Element => {
  const time = getTime(movie.runTime);
  const Linked = ({ children, href }: LinkedProps) => {
    return link ? <Link href={href}>{children}</Link> : children;
  };
  return (
    <MovieDetailSlideStyled>
      <div className="backdrop-image hidden lg:block">
        <Image
          src={movie?.backdrop?.url}
          layout="fill"
          objectFit="cover"
          alt={`${movie?.name} backdrop`}
        />
      </div>
      <div className="backdrop-poster block lg:hidden">
        <Image
          src={movie?.poster?.url}
          layout="fill"
          objectFit="cover"
          alt={`${movie?.name} poster`}
        />
      </div>
      <MovieDetailContentBlockStyled>
        <div className="content-container">
          <div className="movie-poster w-full h-full flex-shrink-0">
            <Linked href={`/movie/${movie.slug}`}>
              <a>
                <Image
                  src={movie?.poster?.url}
                  alt={movie?.name}
                  objectFit="contain"
                  // objectPosition="bottom"
                  layout="fill"
                  className="movie-poster-image"
                />
              </a>
            </Linked>
          </div>
          <div className="movie-details">
            <div className="movie-detail-content">
              <div className="flex flex-wrap">
                {movie?.premiereDate && movie?.status === 'coming-soon' && (
                  <ComingSoonTextStyled>
                    In cinema {`${format(movie?.premiereDate.toDate(), "do 'of' LLLL")}`}
                  </ComingSoonTextStyled>
                )}
                <h2>
                  <Linked href={`/movie/${movie.slug}`}>
                    <a>{movie?.name}</a>
                  </Linked>
                </h2>
                <p>{movie?.overview}</p>
              </div>
              <div className="flex flex-wrap">
                {movie?.genre?.map((item) => (
                  <Badge key={item} content={item} />
                ))}
              </div>
              <ul>
                {movie.certification && (
                  <li>
                    <abbr
                      title={GetRatingDetails(movie?.certification)}
                      style={{ textDecoration: 'none' }}>
                      {movie?.certification}
                    </abbr>
                  </li>
                )}
                {!Number.isNaN(movie.runTime) && movie.runTime > 0 && (
                  <li>{`${time.hours}h ${time.minutes}m`}</li>
                )}
              </ul>
            </div>
            {cinema && (
              <CinemaDetailContentStyled>
                <div className="cinema-detail-outer-wrapper">
                  <div className="cinema-detail-wrapper">
                    <p className="bold">Now showing at KCC Multiplex - {cinema[0]?.label}</p>
                    <ul>
                      <li>{cinema[0]?.features[0]}</li>
                      <li>{cinema[0]?.features[1]}</li>
                    </ul>
                  </div>
                  <div className="flex items-center buy-btn">
                    <Link href={`/buy-tickets?movie=${movie.slug}`}> Buy Tickets </Link>
                  </div>
                </div>
              </CinemaDetailContentStyled>
            )}
          </div>
        </div>
      </MovieDetailContentBlockStyled>
    </MovieDetailSlideStyled>
  );
};

export default MovieDetailSlide;
